import React, { PropsWithChildren, ReactElement, useEffect, useState } from 'react';
import { Image, Platform, ScrollView, StyleSheet, TouchableOpacity, ViewStyle } from 'react-native';
import {
  Disclaimer,
  DISCLAIMER_MODE,
  DNABox,
  DNAButton,
  DNAText,
  colors,
  Iffy,
  InformationMessage,
  luxColors,
  Modal,
} from '@alucio/lux-ui';
import { useAuthenticator } from '@aws-amplify/ui-react-native';
import { isFirefox, isIOS, isMobile, isSafari, isTablet } from 'react-device-detect';
import { WebLinking as Linking } from '@alucio/core';
import { getLegalDocuments, openExternalLink } from 'src/config/legalDocuments';
import { DisclaimerType } from '../Profile/Profile';
import { getAuthenticationLayout } from './authenticationLayout';
import { getAuthenticationKeyboardOffset } from './authenticationViewport';
import LegacyFormWrapper from './LegacyFormWrapper';

const LoginBackground = require('../../../assets/images/LoginBackground.svg');
const BeaconMedispendLoginLogo = require('../../../assets/images/BeaconMedispendLoginLogo.svg');
const FIGMA_CARD_WEB_SHADOW = '0px 18px 64px -8px rgba(115, 57, 230, 0.4), ' +
  '0px 40px 90px -20px rgba(0, 0, 0, 0.45)';
const webCardShadow = Platform.select({
  web: { boxShadow: FIGMA_CARD_WEB_SHADOW } as unknown as ViewStyle,
  default: {},
}) || {};
const webBackgroundLayout = Platform.select({
  web: {
    bottom: 0,
    height: 'auto',
    left: 0,
    marginHorizontal: 0,
    position: 'fixed',
    right: 0,
    top: 0,
  } as unknown as ViewStyle,
  default: {},
}) || {};

const styles = StyleSheet.create({
  backdrop: {
    backgroundColor: luxColors.modalBackdrop.primary,
  },
  background: {
    backgroundColor: luxColors.alucioPurple.primary,
    height: 'var(--beacon-viewport-height, 100vh)',
    marginHorizontal: -16,
    position: 'relative',
    width: '100%',
    ...webBackgroundLayout,
  },
  backgroundImage: {
    height: '100%',
    left: 0,
    position: 'absolute',
    top: 0,
    width: '100%',
  },
  browserWarning: {
    marginBottom: 20,
    width: '100%',
  },
  card: {
    ...webCardShadow,
    backgroundColor: luxColors.info.primary,
    borderColor: 'rgba(255, 255, 255, 0.22)',
    borderRadius: 26,
    borderWidth: 1,
    elevation: 7,
    minHeight: 465,
    paddingBottom: 32,
    paddingHorizontal: 40,
    paddingTop: 40,
    shadowColor: luxColors.alucioPurple.primary,
    shadowOffset: { height: 12, width: 0 },
    shadowOpacity: 0.24,
    shadowRadius: 26,
    width: '100%',
  },
  cardActions: {
    marginTop: 20,
  },
  cardContainer: {
    maxWidth: 548,
    paddingHorizontal: 24,
    width: '100%',
  },
  closeButton: {
    marginVertical: 16,
  },
  content: {
    width: '100%',
  },
  copyright: {
    bottom: 24,
    color: luxColors.info.primary,
    fontSize: 13,
    letterSpacing: 0.2,
    lineHeight: 16,
    opacity: 0.7,
    position: 'absolute',
    textAlign: 'center',
    width: '100%',
  },
  footer: {
    marginTop: 20,
  },
  footerOption: {
    color: '#94A3B8',
    fontFamily: 'Lato',
    fontSize: 14,
    fontWeight: '500',
    lineHeight: 16,
  },
  horizontalOffset: {
    marginHorizontal: -8,
  },
  logo: {
    alignSelf: 'flex-start',
    height: 63,
    marginBottom: 48,
    resizeMode: 'contain',
    width: 143,
  },
  resetPasswordCardActions: {
    marginTop: 12,
  },
  resetPasswordLogo: {
    marginBottom: 24,
  },
  resetPasswordTitle: {
    marginBottom: 12,
  },
  modalContentText: {
    color: luxColors.headerTextColor.primary,
    fontSize: 14,
    lineHeight: 20,
  },
  modalContentWrapper: {
    paddingBottom: 24,
    paddingTop: 16,
  },
  modalFooter: {
    borderBottomWidth: 0,
  },
  modalHeader: {
    paddingBottom: 16,
  },
  modalHeaderText: {
    color: luxColors.blueText.tertiary,
    fontSize: 18,
    fontWeight: 'bold',
  },
  modalSection: {
    borderBottomColor: luxColors.divider.tertiary,
    borderBottomWidth: 1,
    paddingHorizontal: 24,
  },
  modalWrapper: {
    backgroundColor: luxColors.info.primary,
    borderRadius: 8,
    maxWidth: 450,
    paddingTop: 16,
    width: '100%',
  },
  needHelp: {
    color: '#4F46E5',
    fontFamily: 'Lato',
    fontSize: 14,
    fontWeight: '500',
    lineHeight: 16,
    textAlign: 'center',
  },
  needHelpAction: {
    marginLeft: 20,
  },
  scrollContent: {
    alignItems: 'center',
    flexGrow: 1,
    justifyContent: 'center',
    paddingVertical: 24,
    width: '100%',
  },
  scrollRegion: {
    flex: 1,
    width: '100%',
  },
  title: {
    color: colors['color-ui2-neutral-900'],
    fontFamily: 'Lato',
    fontSize: 30,
    fontWeight: '700',
    letterSpacing: -0.3,
    lineHeight: 36,
    marginBottom: 20,
  },
});

interface HelpModalProps {
  toggleHelpModal: () => void;
}

interface Props {
  title?: string;
  isLogin?: boolean;
  showReturnToLogin?: boolean;
  changePasswordEnabled?: boolean;
  resetPassword?: boolean;
}

const V2FormWrapper: React.FC<PropsWithChildren<Props>> = (props) => {
  const [isHelpModalShown, setIsHelpModalShown] = useState<boolean>(false);
  const [keyboardOffset, setKeyboardOffset] = useState<number>(0);
  const [disclaimer, setDisclaimer] =
    useState<DisclaimerType>({ isVisble: false, mode: DISCLAIMER_MODE.Privacy });
  const { toForgotPassword, toSignIn } = useAuthenticator();
  const { copyright, dataPrivacyPolicyUrl } = getLegalDocuments();
  const hasAccountAction = Boolean(
    (props.isLogin && props.changePasswordEnabled) || props.showReturnToLogin,
  );

  useEffect(() => {
    const shouldMoveForKeyboard = isIOS && isTablet;

    if (!shouldMoveForKeyboard || typeof window === 'undefined' || !window.visualViewport) {
      return undefined;
    }

    const visualViewport = window.visualViewport;
    const updateKeyboardOffset = (): void => {
      setKeyboardOffset(getAuthenticationKeyboardOffset({
        isTablet: isIOS && isTablet,
        layoutHeight: window.innerHeight,
        viewportHeight: visualViewport.height,
        viewportOffsetTop: visualViewport.offsetTop,
      }));
    };

    updateKeyboardOffset();
    visualViewport.addEventListener('resize', updateKeyboardOffset);
    visualViewport.addEventListener('scroll', updateKeyboardOffset);

    return () => {
      visualViewport.removeEventListener('resize', updateKeyboardOffset);
      visualViewport.removeEventListener('scroll', updateKeyboardOffset);
    };
  }, []);

  function toggleDisplayModal(): void {
    setIsHelpModalShown((isShown) => !isShown);
  }

  function onCloseDisclaimer(): void {
    setDisclaimer({ ...disclaimer, isVisble: false });
  }

  function showPrivacy(): void {
    setDisclaimer({
      isVisble: true,
      mode: DISCLAIMER_MODE.Privacy,
    });
  }

  async function showDataPrivacyPolicy(): Promise<void> {
    if (!dataPrivacyPolicyUrl) return;

    if (Platform.OS === 'web') {
      openExternalLink(dataPrivacyPolicyUrl);
    } else {
      await Linking.openURL(dataPrivacyPolicyUrl);
    }
  }

  function showToU(): void {
    setDisclaimer({
      isVisble: true,
      mode: DISCLAIMER_MODE.ToU,
    });
  }

  return (
    <DNABox
      testID="authentication-background"
      style={styles.background}
      appearance="col"
      alignX="center"
      alignY="center"
      fill
    >
      <Image source={LoginBackground} style={styles.backgroundImage} resizeMode="cover" />
      {disclaimer.isVisble
        ? <Disclaimer mode={disclaimer.mode} onClose={onCloseDisclaimer} />
        : null
      }
      <Iffy is={isHelpModalShown}>
        <HelpModal toggleHelpModal={toggleDisplayModal} />
      </Iffy>
      <ScrollView
        testID="authentication-scroll-region"
        style={styles.scrollRegion}
        contentContainerStyle={styles.scrollContent}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
      >
        <DNABox
          testID="authentication-card-container"
          style={styles.cardContainer}
          appearance="col"
          alignX="center"
        >
          <DNABox
            testID="authentication-card"
            style={[styles.card, { transform: [{ translateY: -(keyboardOffset / 2) }] }]}
            appearance="col"
            alignX="stretch"
          >
            <DNABox style={styles.content} appearance="col" alignX="stretch" fill>
              <Iffy is={isFirefox || (!isMobile && isSafari)}>
                <InformationMessage
                  variance="warning"
                  customWrapperStyle={[styles.horizontalOffset, styles.browserWarning]}
                  text="For the best user experience please use the latest version of the Chrome web browser."
                />
              </Iffy>
              <Image
                testID="authentication-logo"
                source={BeaconMedispendLoginLogo}
                style={[styles.logo, props.resetPassword ? styles.resetPasswordLogo : undefined]}
              />
              <Iffy is={Boolean(props.title)}>
                <DNAText style={[styles.title, props.resetPassword ? styles.resetPasswordTitle : undefined]}>
                  {props.title}
                </DNAText>
              </Iffy>
              {props.children}
              <DNABox
                testID="authentication-card-actions"
                style={[
                  styles.cardActions,
                  props.resetPassword ? styles.resetPasswordCardActions : undefined,
                ]}
                appearance="row"
                alignX="center"
                alignY="center"
              >
                <Iffy is={Boolean(props.isLogin && props.changePasswordEnabled)}>
                  <TouchableOpacity testID="forgot-password" onPress={toForgotPassword}>
                    <DNAText style={styles.needHelp}>Forgot password?</DNAText>
                  </TouchableOpacity>
                </Iffy>
                <Iffy is={Boolean(props.showReturnToLogin)}>
                  <TouchableOpacity onPress={toSignIn}>
                    <DNAText style={styles.needHelp}>Log in</DNAText>
                  </TouchableOpacity>
                </Iffy>
                <TouchableOpacity
                  style={hasAccountAction ? styles.needHelpAction : undefined}
                  onPress={toggleDisplayModal}
                >
                  <DNAText style={styles.needHelp}>Need help?</DNAText>
                </TouchableOpacity>
              </DNABox>
            </DNABox>
            <DNABox
              testID="authentication-footer"
              style={styles.footer}
              appearance="row"
              alignX="center"
              alignY="center"
            >
              {dataPrivacyPolicyUrl ? (
                <TouchableOpacity onPress={showDataPrivacyPolicy}>
                  <DNAText style={styles.footerOption}>Data Privacy Policy</DNAText>
                </TouchableOpacity>
              ) : (
                <>
                  <TouchableOpacity onPress={showToU}>
                    <DNAText style={styles.footerOption}>Terms of Use</DNAText>
                  </TouchableOpacity>
                  <TouchableOpacity onPress={showPrivacy}>
                    <DNAText style={styles.footerOption}>Privacy Policy</DNAText>
                  </TouchableOpacity>
                </>
              )}
            </DNABox>
          </DNABox>
        </DNABox>
      </ScrollView>
      <DNAText testID="authentication-copyright" style={styles.copyright}>
        {dataPrivacyPolicyUrl
          ? `© ${new Date().getFullYear()} ${copyright}`
          : `Copyright ${new Date().getFullYear()} ${copyright}`}
      </DNAText>
    </DNABox>
  );
};

const FormWrapper: React.FC<PropsWithChildren<Props>> = (props) => {
  const authenticationLayout = getAuthenticationLayout(
    typeof window === 'undefined' ? undefined : window.location.search,
  );

  return authenticationLayout === 'legacy'
    ? <LegacyFormWrapper {...props} />
    : <V2FormWrapper {...props} />;
};

const HelpModal = (props: HelpModalProps): ReactElement => {
  const { toggleHelpModal } = props;

  return (
    <Modal visible={true} onBackdropPress={toggleHelpModal} backdropStyle={styles.backdrop}>
      <DNABox testID="v2-help-modal" style={styles.modalWrapper} appearance="col">
        <DNABox
          style={[styles.modalSection, styles.modalHeader]}
          appearance="row"
          alignY="center"
          spacing="between"
        >
          <DNAText h5 bold style={styles.modalHeaderText}>Need help?</DNAText>
          <DNAButton iconLeft="x" appearance="ghost" status="subtle" onPress={toggleHelpModal} />
        </DNABox>
        <DNABox
          style={[styles.modalSection, styles.modalContentWrapper]}
          appearance="row"
          wrap="stretch"
          fill
        >
          <DNAText style={styles.modalContentText}>
            Please contact Beacon Support
            <DNAText bold> (support@beacon.medispend.com)</DNAText>
            {' for assistance with your account.'}
          </DNAText>
        </DNABox>
        <DNABox
          style={[styles.modalSection, styles.modalFooter]}
          appearance="row"
          alignX="end"
        >
          <DNAButton style={styles.closeButton} onPress={toggleHelpModal}>
            Close
          </DNAButton>
        </DNABox>
      </DNABox>
    </Modal>
  );
};

export default FormWrapper;
